package demos.switch

import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.Orientation
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.ExperimentalMaterialApi
import androidx.compose.material.FractionalThreshold
import androidx.compose.material.rememberSwipeableState
import androidx.compose.material.swipeable
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.BlurredEdgeTreatment
import androidx.compose.ui.draw.blur
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import kotlin.math.roundToInt


const val REAL_SWITCH = "c8e7d95787cca3fcbc8fe8bd6752058c8030a0e5b317844b15a9ff31cd5e32a9"

@Composable
fun RealSwitch() {
    var checked by remember { mutableStateOf(false) }
    RealSwitchComponent(
        checked = checked,
        onCheckedChange = {
            checked = it
        }
    )
}

@OptIn(ExperimentalMaterialApi::class)
@Composable
fun RealSwitchComponent(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    shape: Shape = CircleShape,
) {

    Box(
        Modifier
            .fillMaxSize()
            .background(Color.White),
        contentAlignment = Alignment.Center
    ) {

        val totalWidth = 192.dp
        val thumbSize = 96.dp
        val thumbSizePx = with(LocalDensity.current) { thumbSize.toPx() }

        val scope = rememberCoroutineScope()
        val swipeState = rememberSwipeableState(
            if (checked) 1 else 0,
            confirmStateChange = {
                onCheckedChange(it == 1)
                true
            })
        val anchors = mapOf(0f to 0, thumbSizePx to 1)

        val checkedState = rememberUpdatedState(checked)
        LaunchedEffect(checked) {
            scope.launch {
                swipeState.animateTo(
                    targetValue = if (checked) 1 else 0,
                    anim = spring(
                        stiffness = Spring.StiffnessMedium,
                    )
                )
            }
        }

        Box(
            Modifier
                .width(totalWidth)
                .height(thumbSize)
                .background(
                    color = Color(0xffdedede),
                    shape = shape
                )
                .pointerInput(Unit) {
                    detectTapGestures {
                        onCheckedChange(!checkedState.value)
                    }
                }
                .swipeable(
                    state = swipeState,
                    anchors = anchors,
                    thresholds = { _, _ -> FractionalThreshold(0.3f) },
                    orientation = Orientation.Horizontal
                )
        ) {

            Box(
                Modifier
                    .scale(scaleX = 1.2f, scaleY = 1.3f)
                    .background(
                        brush = Brush.verticalGradient(
                            colors = listOf(
                                Color.Black.copy(alpha = .04f),
                                Color.Black.copy(alpha = .02f),
                            )
                        ),
                        shape = shape,
                    )
                    .width(totalWidth)
                    .height(thumbSize)
            )

            Box(
                Modifier
                    .fillMaxSize()
                    .clip(shape)
                    .offset {
                        IntOffset(
                            x = (-totalWidth.roundToPx() + swipeState.offset.value + thumbSizePx / 2).roundToInt(),
                            y = 0,
                        )
                    }
                    .background(
                        color = Color(0xff34f768),
                    )
            )
            Box(
                Modifier
                    .fillMaxSize()
                    .clip(shape)
                    .blur(
                        radius = 5.dp,
                        edgeTreatment = BlurredEdgeTreatment.Unbounded,
                    )
                    .border(
                        width = 2.dp,
                        color = Color.Black.copy(alpha = .4f),
                        shape = shape,
                    )
            )

            Box(Modifier
                .offset {
                    IntOffset(
                        x = swipeState.offset.value.roundToInt(),
                        y = 0,
                    )
                }
                .size(thumbSize)
            ) {
                Box(
                    Modifier
                        .blur(
                            radius = 6.dp,
                            edgeTreatment = BlurredEdgeTreatment.Unbounded,
                        )
                        .fillMaxSize()
                        .background(Color.Black.copy(alpha = .4f), shape)
                )
                Box(
                    Modifier
                        .border(
                            width = 4.dp,
                            shape = shape,
                            brush = Brush.verticalGradient(
                                colors = listOf(
                                    Color.White.copy(alpha = .8f),
                                    Color.Black.copy(alpha = .15f),
                                )
                            )
                        )
                        .fillMaxSize()
                        .background(Color.White, shape)
                        .padding(2.dp)
                        .clip(shape)
                        .background(
                            brush = Brush.radialGradient(
                                listOf(
                                    Color.Transparent,
                                    Color.Transparent,
                                    Color.Black.copy(alpha = .05f),
                                )
                            )
                        )
                )
            }
        }
    }
}